import { useCallback, useEffect, useState } from 'react';
import { api, type BelgeArsivSatiri, type BelgeArsivSorgu, type Paged } from './api';
import { BELGELER as M } from './metin/belgeler';
import { tamSayi } from './metin/ortak';
import { tarihSaatBae } from './web-sitesi-bicim';
import './belgeler.css';

const SAYFA_BOYU = 20;

function boyut(bayt: number): string {
  if (bayt >= 1024 * 1024) return `${(bayt / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
  return `${tamSayi(Math.max(1, Math.round(bayt / 1024)))} KB`;
}

function turAdi(satir: BelgeArsivSatiri): string {
  if (satir.sourceDocumentId) return M.duzenlenmis(satir.versionNo);
  return M.turler[satir.kind] ?? satir.kind;
}

function durumMetni(satir: BelgeArsivSatiri): string {
  if (!satir.fileAvailable) return M.imhaEdildi(tarihSaatBae(satir.purgedAt ?? satir.uploadedAt).slice(0, 10));
  if (satir.purgeAt) return M.silinecek(tarihSaatBae(satir.purgeAt).slice(0, 10));
  if (satir.linked) return M.saklanir;
  return M.hazir;
}

export function Belgeler({ canWrite }: { canWrite: boolean }) {
  const [girdi, setGirdi] = useState('');
  const [sorgu, setSorgu] = useState<BelgeArsivSorgu>({ page: 1, pageSize: SAYFA_BOYU });
  const [sonuc, setSonuc] = useState<Paged<BelgeArsivSatiri> | null>(null);
  const [hata, setHata] = useState(false);
  const [yenile, setYenile] = useState(0);
  const [indiriliyor, setIndiriliyor] = useState<string | null>(null);
  const [mesaj, setMesaj] = useState<{ tur: 'ok' | 'hata'; metin: string } | null>(null);
  const [baglanan, setBaglanan] = useState<string | null>(null);
  const [siparisTuru, setSiparisTuru] = useState<'crm' | 'web'>('crm');
  const [siparisNo, setSiparisNo] = useState('');
  const [baglaniyor, setBaglaniyor] = useState(false);

  useEffect(() => {
    let aktif = true;
    setHata(false);
    void api.belgeArsivi(sorgu).then((veri) => { if (aktif) setSonuc(veri); }).catch(() => { if (aktif) setHata(true); });
    return () => { aktif = false; };
  }, [sorgu, yenile]);

  const degistir = useCallback((parca: { [K in keyof BelgeArsivSorgu]?: BelgeArsivSorgu[K] | undefined }) => {
    setSonuc(null);
    setSorgu((onceki) => {
      const yeni: Record<string, unknown> = { ...onceki, ...parca, page: 1 };
      for (const anahtar of Object.keys(yeni)) if (yeni[anahtar] === undefined) delete yeni[anahtar];
      return yeni as BelgeArsivSorgu;
    });
  }, []);

  const indir = async (satir: BelgeArsivSatiri) => {
    setIndiriliyor(satir.id); setMesaj(null);
    try { await api.downloadTaskDocument(satir.id, satir.fileName); } catch { setMesaj({ tur: 'hata', metin: M.indirmeHatasi }); } finally { setIndiriliyor(null); }
  };

  const bagla = async (satir: BelgeArsivSatiri) => {
    const no = siparisNo.trim();
    if (!no) return;
    setBaglaniyor(true); setMesaj(null);
    try {
      await api.belgeSipariseBagla(satir.id, siparisTuru === 'crm' ? { orderCode: no } : { webOrderReference: no });
      setMesaj({ tur: 'ok', metin: M.baglandi(satir.fileName, no) });
      setBaglanan(null); setSiparisNo('');
      setYenile((n) => n + 1);
    } catch (error) {
      setMesaj({ tur: 'hata', metin: error instanceof Error ? error.message : M.hata });
    } finally { setBaglaniyor(false); }
  };

  const toplamSayfa = sonuc ? Math.max(1, Math.ceil(sonuc.total / sonuc.pageSize)) : 1;
  const sayfa = sorgu.page ?? 1;

  return <section className="belgeler" data-testid="belge-arsivi">
    <p className="belgeler-aciklama">{M.aciklama}</p>
    <form className="belgeler-suzgec" onSubmit={(olay) => { olay.preventDefault(); degistir({ q: girdi.trim() || undefined }); }}>
      <label>{M.ara}<input type="search" data-testid="belge-ara" value={girdi} placeholder={M.araYer} maxLength={80} onChange={(olay) => setGirdi(olay.target.value)} /></label>
      <label>{M.tur}<select data-testid="belge-tur" value={sorgu.kind ?? ''} onChange={(olay) => degistir({ kind: olay.target.value || undefined })}>
        <option value="">{M.turHepsi}</option>
        {Object.entries(M.turler).map(([kod, ad]) => <option key={kod} value={kod}>{ad}</option>)}
      </select></label>
      <label>{M.baglanti}<select data-testid="belge-baglanti" value={sorgu.linked ?? ''} onChange={(olay) => degistir({ linked: (olay.target.value || undefined) as BelgeArsivSorgu['linked'] })}>
        <option value="">{M.baglantiHepsi}</option><option value="yes">{M.baglantiEvet}</option><option value="no">{M.baglantiHayir}</option>
      </select></label>
      <label>{M.durum}<select data-testid="belge-durum" value={sorgu.state ?? ''} onChange={(olay) => degistir({ state: (olay.target.value || undefined) as BelgeArsivSorgu['state'] })}>
        <option value="">{M.durumHepsi}</option><option value="available">{M.durumHazir}</option><option value="purged">{M.durumImha}</option>
      </select></label>
      <button type="submit" data-testid="belge-ara-dugme">{M.ara}</button>
    </form>
    {mesaj ? <p role={mesaj.tur === 'hata' ? 'alert' : 'status'} className={`belgeler-mesaj belgeler-mesaj--${mesaj.tur}`} data-testid="belge-mesaj">{mesaj.metin}</p> : null}
    {hata ? <div role="alert"><p>{M.hata}</p><button type="button" onClick={() => setYenile((n) => n + 1)}>{M.yeniden}</button></div> : null}
    {!hata && !sonuc ? <p role="status">{M.yukleniyor}</p> : null}
    {sonuc && sonuc.items.length === 0 ? <p data-testid="belge-bos">{M.bos}</p> : null}
    {sonuc && sonuc.items.length > 0 ? <>
      <div className="belgeler-tablo-kap">
        <table className="belgeler-tablo" data-testid="belge-tablo">
          <thead><tr>{Object.values(M.sutunlar).map((ad) => <th key={ad} scope="col">{ad}</th>)}</tr></thead>
          <tbody>{sonuc.items.map((satir) => <tr key={satir.id} data-testid={`belge-satir-${satir.id}`}>
            <td className="belgeler-dosya">{satir.fileName}</td>
            <td>{turAdi(satir)}</td>
            <td>{satir.orderCode ?? satir.webOrderReference ?? M.siparisYok}</td>
            <td>{satir.customerName ?? ''}</td>
            <td>{boyut(satir.byteSize)}</td>
            <td>{tarihSaatBae(satir.uploadedAt)}</td>
            <td data-testid={`belge-durum-${satir.id}`}>{durumMetni(satir)}</td>
            <td className="belgeler-islem">
              <button type="button" data-testid={`belge-indir-${satir.id}`} disabled={!satir.fileAvailable || indiriliyor !== null} title={satir.fileAvailable ? undefined : M.indirilemez}
                onClick={() => void indir(satir)}>{indiriliyor === satir.id ? M.indiriliyor : M.indir}</button>
              {canWrite && !satir.linked && satir.fileAvailable ? <button type="button" className="ikincil" data-testid={`belge-bagla-${satir.id}`}
                onClick={() => { setBaglanan(baglanan === satir.id ? null : satir.id); setSiparisNo(''); setMesaj(null); }}>{M.bagla}</button> : null}
              {baglanan === satir.id ? <form className="belgeler-bagla" onSubmit={(olay) => { olay.preventDefault(); void bagla(satir); }}>
                <select aria-label={M.baglaBaslik} data-testid="belge-siparis-turu" value={siparisTuru} onChange={(olay) => setSiparisTuru(olay.target.value as 'crm' | 'web')}>
                  <option value="crm">{M.crmSiparis}</option><option value="web">{M.webSiparis}</option>
                </select>
                <input aria-label={M.siparisNo} data-testid="belge-siparis-no" value={siparisNo} maxLength={80} placeholder={M.siparisNo} onChange={(olay) => setSiparisNo(olay.target.value)} />
                <button type="submit" data-testid="belge-bagla-onayla" disabled={baglaniyor || siparisNo.trim() === ''}>{M.baglaOnayla}</button>
                <button type="button" className="ikincil" onClick={() => setBaglanan(null)}>{M.vazgec}</button>
                <small>{M.baglaNot}</small>
              </form> : null}
            </td>
          </tr>)}</tbody>
        </table>
      </div>
      <nav className="belgeler-sayfa" aria-label={M.baslik}>
        <button type="button" disabled={sayfa <= 1} onClick={() => { setSonuc(null); setSorgu((o) => ({ ...o, page: sayfa - 1 })); }}>{M.onceki}</button>
        <span data-testid="belge-sayfa">{M.sayfa(sayfa, toplamSayfa, sonuc.total)}</span>
        <button type="button" disabled={sayfa >= toplamSayfa} onClick={() => { setSonuc(null); setSorgu((o) => ({ ...o, page: sayfa + 1 })); }}>{M.sonraki}</button>
      </nav>
    </> : null}
  </section>;
}
